import { useCdpUrl } from "@plutoxyz/react-frame";
import { useState, useEffect, useRef } from "react";
import { BrowserView, BrowserViewRef } from "@plutoxyz/devtools";

const BrowserInspector = ({
  onComplete,
  refresh,
}: {
  onComplete: () => void;
  refresh: boolean;
}) => {
  const cdpUrl = useCdpUrl();
  const [cdpUrls, setCdpUrls] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(null);
  const [connectedStates, setConnectedStates] = useState<boolean[]>([]);
  const [pageTitles, setPageTitles] = useState<Record<string, string>>({});
  const browserViewContainerRef = useRef<HTMLDivElement>(null);

  // Add new CDP URL when it changes
  useEffect(() => {
    if (cdpUrl && !cdpUrls.includes(cdpUrl)) {
      setCdpUrls([...cdpUrls, cdpUrl]);
      setActiveTab(cdpUrl);
    }
  }, [cdpUrl]);

  useEffect(() => {
    setCdpUrls([]);
  }, [refresh]);

  // Initialize refs array and pageTitles when cdpUrls changes
  useEffect(() => {
    // New Tab Opened
    if (cdpUrls.length > connectedStates.length) {
      setConnectedStates((prev) => {
        const newStates = [...prev];
        newStates.push(false);
        return newStates;
      });
      handleTitleChange(cdpUrl, "");
    }
  }, [cdpUrls.length]);

  const handleTitleChange = (url: string, title: string) => {
    setPageTitles((prev) => {
      const newTitles = { ...prev };
      newTitles[url] = title;
      return newTitles;
    });
  };

  const handleConnectionChange = async (index: number, connected: boolean) => {
    setConnectedStates((prev) => {
      const newStates = [...prev];
      newStates[index] = connected;
      return newStates;
    });

    if (!connected) {
      setCdpUrls((prev) => {
        const newUrls = [...prev];
        newUrls.splice(index, 1);

        if (newUrls.length === 0) {
          onComplete();
        }

        return newUrls;
      });

      setActiveTab(null);

      return;
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      {cdpUrls.length > 1 && (
        <div className="min-h-12 flex  border-gray-300 bg-white px-2 pt-2">
          {cdpUrls.map((url, index) => (
            <button
              key={url}
              onClick={() => setActiveTab(url)}
              className={`flex items-center gap-20 px-4 py-1 mr-1 rounded-t-lg text-sm font-medium transition-colors
              ${
                activeTab === url
                  ? "bg-white border-x border-t border-b-0 border-gray-300 text-gray-900 shadow-sm"
                  : "bg-gray-100 border-x border-t border-b-0 border-transparent text-gray-500 hover:text-gray-900 hover:bg-white"
              }
            `}
              style={{
                borderBottom:
                  activeTab === url ? "none" : "1px solid transparent",
                minWidth: 240,
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={pageTitles[url] || "New Tab"}
            >
              <span className="truncate">
                {pageTitles[url] ? pageTitles[url] : "New Tab"}
              </span>
            </button>
          ))}
        </div>
      )}
      <div
        ref={browserViewContainerRef}
        className="flex-1 h-full relative bg-white"
      >
        {cdpUrls.map((url, index) => (
          <div
            key={url}
            className={`absolute inset-0 ${
              activeTab === url ? "block" : "hidden"
            }`}
          >
            <BrowserView
              wsUrl={url}
              onConnectionChange={(connected) =>
                handleConnectionChange(index, connected)
              }
              onPageTitleChange={(title) => handleTitleChange(url, title)}
            />
          </div>
        ))}

        {cdpUrls.length === 0 && (
          <div className={`flex items-center justify-center h-full`}>
            <div className="text-center max-w-sm mx-auto px-6">
              <span className="w-full h-full flex items-center justify-center">
                <div className="w-10 h-10 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
              </span>
              <h3 className="text-base font-medium text-gray-900 mb-2">
                Ready to Debug
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Click "Run" to get started
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowserInspector;
